// Argus — AgentSettings/Notes/Tags/Metrics API helpers.

import { apiFetch } from './api';

// ---- Settings ----
export interface AgentSettings {
  agentId: string;
  autoRecordOnConnect: boolean;
  autoRecordSources: string[] | null;
  autoRecordRotateSeconds: number | null;
  customRetentionDays: number | null;
  heartbeatIntervalSeconds: number;
  thumbnailIntervalSeconds: number;
  updatedAt: string | null;
}
export interface AgentSettingsPatch {
  autoRecordOnConnect?: boolean;
  autoRecordSources?: string[] | null;
  autoRecordRotateSeconds?: number | null;
  customRetentionDays?: number | null;
  heartbeatIntervalSeconds?: number;
  thumbnailIntervalSeconds?: number;
}

export async function fetchAgentSettings(agentId: string): Promise<AgentSettings> {
  const r = await apiFetch<AgentSettings>(`/api/v1/agents/${agentId}/settings`);
  return r.data;
}
export async function patchAgentSettings(agentId: string, patch: AgentSettingsPatch): Promise<AgentSettings> {
  const r = await apiFetch<AgentSettings>(`/api/v1/agents/${agentId}/settings`, { method: 'PATCH', body: JSON.stringify(patch) });
  return r.data;
}

// ---- Notes ----
export interface AgentNotes {
  agentId: string;
  contentMd: string;
  updatedAt: string | null;
}
export async function fetchAgentNotes(agentId: string): Promise<AgentNotes> {
  const r = await apiFetch<AgentNotes>(`/api/v1/agents/${agentId}/notes`);
  return r.data;
}
export async function putAgentNotes(agentId: string, contentMd: string): Promise<AgentNotes> {
  const r = await apiFetch<AgentNotes>(`/api/v1/agents/${agentId}/notes`, { method: 'PUT', body: JSON.stringify({ contentMd }) });
  return r.data;
}

// ---- Tags ----
export interface AgentTag {
  id: string;
  name: string;
  color: string | null;
  createdAt?: string;
  createdBy?: string;
  addedAt?: string;
}
export async function fetchAllTags(): Promise<AgentTag[]> {
  const r = await apiFetch<{ items: AgentTag[] }>('/api/v1/tags');
  return r.data.items;
}
export async function fetchAgentTags(agentId: string): Promise<AgentTag[]> {
  const r = await apiFetch<{ items: AgentTag[] }>(`/api/v1/agents/${agentId}/tags`);
  return r.data.items;
}
export async function createTag(name: string, color?: string): Promise<AgentTag> {
  const r = await apiFetch<AgentTag>('/api/v1/tags', { method: 'POST', body: JSON.stringify({ name, color }) });
  return r.data;
}
export async function deleteTag(tagId: string): Promise<void> {
  await apiFetch(`/api/v1/tags/${tagId}`, { method: 'DELETE' });
}
export async function linkTagToAgent(agentId: string, tagId: string): Promise<void> {
  await apiFetch(`/api/v1/agents/${agentId}/tags`, { method: 'POST', body: JSON.stringify({ tagId }) });
}
export async function unlinkTagFromAgent(agentId: string, tagId: string): Promise<void> {
  await apiFetch(`/api/v1/agents/${agentId}/tags/${tagId}`, { method: 'DELETE' });
}

// ---- Metrics ----
export interface MetricsBucket {
  bucket: string;
  cpuPct: number | null;
  ramUsedMb: number | null;
  diskFreeGb: number | null;
  samples: number;
}
export interface AgentMetricsResponse {
  agentId: string;
  from: string;
  to: string;
  interval: number;
  items: MetricsBucket[];
}
export async function fetchAgentMetrics(agentId: string, opts: { interval?: number; fromIso?: string; toIso?: string } = {}): Promise<AgentMetricsResponse> {
  const params = new URLSearchParams();
  if (opts.interval !== undefined) params.set('interval', String(opts.interval));
  if (opts.fromIso !== undefined) params.set('from', opts.fromIso);
  if (opts.toIso !== undefined) params.set('to', opts.toIso);
  const qs = params.toString() === '' ? '' : `?${params.toString()}`;
  const r = await apiFetch<AgentMetricsResponse>(`/api/v1/agents/${agentId}/metrics${qs}`);
  return r.data;
}
